import React, { useEffect, useState } from 'react';
import { Badge, Card, SectionTitle } from '../../design-system';
import { useConfirmAction } from '../ui/confirmactionhandler';
import { formatDate } from '../../utils/formatters';
import {
  apiErrorDetails,
  moveLessonPointer,
  useFirmLessons,
  useLessonFirms,
  useLessonHistory,
} from '../../api/lessonreview';
import { categoryLabel, displayName, POINTER_ACTION_LABELS } from './lessonReviewLabels';
import styles from './LessonReview.module.css';

/** How a live lesson has held up in the firm's chats (Learning Loop 2.1): counts only, from Livi's reviews of
 * past work. "Not working" = corrected repeatedly since it last helped — worth a look. */
function Scorecard({ score }) {
  const kept = Number(score?.kept || 0);
  const corrected = Number(score?.corrected || 0);
  if (!score?.not_working && kept === 0 && corrected === 0) {
    return <span className={styles.muted}>No evidence yet</span>;
  }
  return (
    <div className={styles.scoreLine}>
      {score?.not_working ? <Badge variant="warning" size="sm">Not working</Badge> : null}
      <span>Kept {kept} · Corrected {corrected}</span>
      {score?.last_evidence_at ? (
        <span className={styles.muted}>last {formatDate(score.last_evidence_at)}</span>
      ) : null}
    </div>
  );
}

/** One firm lesson's versions + pointer history, with rollback / restore / retire. */
function LessonHistoryDrawer({ contentKey, lessonId, onClose }) {
  const { data, error, isLoading } = useLessonHistory(contentKey, lessonId);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const retired = data?.status === 'retired';
  const liveVersion = data?.live_version;

  async function doMove({ toVersion, verb }) {
    setBusy(true);
    setActionError(null);
    try {
      await moveLessonPointer(contentKey, lessonId, {
        toVersion, expectedLiveVersion: liveVersion, reason: reason.trim(),
      });
      setReason('');
      setMessage(`${verb}. Open chats pick up the change within about a minute.`);
    } catch (err) {
      setActionError(apiErrorDetails(err, 'The change did not go through.'));
    } finally {
      setBusy(false);
    }
  }

  const { handleConfirmAction: confirmMove, ConfirmDialogElement: MoveDialog } = useConfirmAction(doMove, {
    title: 'Change which version is live',
    getEntityName: (d) => d?.label,
    confirmMessage: 'This changes what Livi uses for every case this firm owns. The history is kept.',
    proceedButtonLabel: 'Confirm',
    confirmKeyword: 'confirm',
  });

  const title = (data?.versions || []).find((v) => v.version === liveVersion)?.title || lessonId;
  const needReason = !reason.trim();

  return (
    <>
      <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
      <aside className={styles.drawer} role="dialog" aria-modal="true" aria-label="Firm lesson history">
        <div className={styles.drawerHeader}>
          <div>
            <h2 className={styles.drawerTitle}>{title}</h2>
            {data ? (
              <div className={styles.badgeRow}>
                <Badge variant={retired ? 'default' : 'success'} size="sm">
                  {retired ? 'Retired' : `Live: version ${liveVersion}`}
                </Badge>
              </div>
            ) : null}
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close lesson history">
            ✕
          </button>
        </div>
        <div className={styles.drawerBody}>
          {isLoading ? <p className={styles.emptyText}>Loading…</p> : null}
          {error ? (
            <div className={styles.errorBanner} role="alert">
              {apiErrorDetails(error, 'This lesson could not be loaded.').message}
            </div>
          ) : null}
          {message ? <div className={styles.successBanner} role="status">{message}</div> : null}
          {actionError ? <div className={styles.errorBanner} role="alert">{actionError.message}</div> : null}

          {data ? (
            <>
              <section className={styles.drawerSection}>
                <SectionTitle>Change the live version</SectionTitle>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="lesson-move-reason">Reason (required)</label>
                  <textarea id="lesson-move-reason" className={styles.textarea} rows={2} maxLength={500}
                            value={reason} onChange={(e) => setReason(e.target.value)} />
                </div>
                {!retired ? (
                  <div className={styles.actionRow}>
                    <button type="button" className={styles.dangerBtn} disabled={busy || needReason}
                            onClick={() => confirmMove({ toVersion: null, verb: 'Retired',
                                                         label: `Retire “${title}”` })}>
                      Retire lesson
                    </button>
                  </div>
                ) : null}
              </section>

              <section className={styles.drawerSection}>
                <SectionTitle>Versions</SectionTitle>
                {[...(data.versions || [])].reverse().map((v) => {
                  const isLive = !retired && v.version === liveVersion;
                  const verb = retired ? 'Restored' : 'Rolled back';
                  return (
                    <div key={v.version} className={`${styles.versionCard} ${isLive ? styles.versionLive : ''}`}>
                      <div className={styles.versionHeader}>
                        <span>
                          Version {v.version}
                          {isLive ? ' (live)' : ''}
                        </span>
                        <span className={styles.timelineMeta}>
                          {v.ts ? formatDate(v.ts) : ''}
                          {v.actor ? ` · approved by ${displayName(v.actor_name, 'person')}` : ''}
                        </span>
                      </div>
                      <p className={styles.lessonBody}>{v.body}</p>
                      {!isLive ? (
                        <div className={styles.actionRow}>
                          <button type="button" className={styles.secondaryBtn} disabled={busy || needReason}
                                  onClick={() => confirmMove({
                                    toVersion: v.version, verb,
                                    label: `${retired ? 'Restore' : 'Roll back to'} version ${v.version}`,
                                  })}>
                            {retired ? `Restore version ${v.version}` : `Roll back to version ${v.version}`}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </section>

              <section className={styles.drawerSection}>
                <SectionTitle>What happened</SectionTitle>
                <ol className={styles.timeline}>
                  {[...(data.audit || [])].reverse().map((a, i) => (
                    <li key={`${a.ts}-${a.action}-${i}`} className={styles.timelineItem}>
                      <span>
                        {POINTER_ACTION_LABELS[a.action] || a.action}
                        {a.to_version ? ` → version ${a.to_version}` : ''}
                      </span>
                      <span className={styles.timelineMeta}>
                        {a.ts ? formatDate(a.ts) : ''}{a.actor ? ` · ${displayName(a.actor_name, 'person')}` : ''}
                        {a.reason ? ` · ${a.reason}` : ''}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            </>
          ) : null}
        </div>
        <MoveDialog />
      </aside>
    </>
  );
}

export default function FirmLessonsPanel({ enabled = true }) {
  const { data: firmsData, error: firmsError } = useLessonFirms(enabled);
  const [contentKey, setContentKey] = useState('');
  const [openLessonId, setOpenLessonId] = useState(null);
  const { data: lessonsData, error: lessonsError, isLoading } = useFirmLessons(contentKey, enabled);

  const firms = firmsData?.firms || [];
  const lessons = lessonsData?.lessons || [];

  return (
    <section className={styles.section}>
      <SectionTitle>Firm lessons</SectionTitle>
      <p className={styles.intro}>
        Lessons that apply to every case a firm owns. Pick a firm to see its lessons, their versions, and
        who changed them.
      </p>
      {firmsError ? (
        <div className={styles.errorBanner} role="alert">
          {apiErrorDetails(firmsError, 'The firm list could not be loaded.').message}
        </div>
      ) : null}
      <div className={styles.pickerRow}>
        <label className={styles.label} htmlFor="lesson-firm-picker">Firm</label>
        <select id="lesson-firm-picker" className={styles.select} value={contentKey}
                onChange={(e) => { setContentKey(e.target.value); setOpenLessonId(null); }}>
          <option value="">{firms.length ? 'Choose a firm…' : 'No firm has lessons yet'}</option>
          {firms.map((f) => (
            <option key={f.content_key} value={f.content_key}>
              {displayName(f.name, 'firm')}{f.has_active_lessons ? '' : ' (no live lessons)'}
            </option>
          ))}
        </select>
      </div>

      {contentKey ? (
        <>
          {lessonsError ? (
            <div className={styles.errorBanner} role="alert">
              {apiErrorDetails(lessonsError, "This firm's lessons could not be loaded.").message}
            </div>
          ) : null}
          {isLoading ? <p className={styles.emptyText}>Loading…</p> : null}
          {!isLoading && lessons.length === 0 && !lessonsError ? (
            <Card padding="md"><p className={styles.emptyText}>This firm has no lessons.</p></Card>
          ) : null}
          {lessons.length > 0 ? (
            <Card padding="none" className={styles.tableCard}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>Lesson</th>
                    <th className={styles.th}>Category</th>
                    <th className={styles.th}>Always apply</th>
                    <th className={styles.th}>Status</th>
                    <th className={styles.th}>How it&apos;s holding up</th>
                    <th className={styles.th}>Last changed</th>
                  </tr>
                </thead>
                <tbody>
                  {lessons.map((l) => (
                    <tr key={l.lesson_id} className={styles.row} tabIndex={0}
                        onClick={() => setOpenLessonId(l.lesson_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenLessonId(l.lesson_id);
                          }
                        }}
                        aria-label={`Open firm lesson: ${l.title}`}>
                      <td className={`${styles.td} ${styles.titleCell}`}>{l.title}</td>
                      <td className={styles.td}>{categoryLabel(l.category)}</td>
                      <td className={styles.td}>{l.mandatory ? 'Yes' : 'No'}</td>
                      <td className={styles.td}>
                        <Badge variant={l.status === 'active' ? 'success' : 'default'} size="sm">
                          {l.status === 'active' ? `Live (v${l.live_version})` : 'Retired'}
                        </Badge>
                      </td>
                      <td className={styles.td}><Scorecard score={l.scorecard} /></td>
                      <td className={`${styles.td} ${styles.nowrap}`}>
                        {l.updated_at ? formatDate(l.updated_at) : '—'}
                        {l.updated_by ? (
                          <div className={styles.muted}>by {displayName(l.updated_by_name, 'person')}</div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}
        </>
      ) : null}

      {openLessonId ? (
        <LessonHistoryDrawer contentKey={contentKey} lessonId={openLessonId}
                             onClose={() => setOpenLessonId(null)} />
      ) : null}
    </section>
  );
}
